import * as PIXI from 'pixi.js';
import { Config, mergeConfig } from './Config.js';
import { EventBus, GameEvents, globalEventBus } from './EventBus.js';
import { PuzzleBoard } from '../puzzle/PuzzleBoard.js';
import { ImageLoader } from '../utils/ImageLoader.js';
import { UIManager } from '../ui/UIManager.js';
import { Timer } from '../ui/Timer.js';
import { ScoreBoard } from '../ui/ScoreBoard.js';
import { DragController } from '../input/DragController.js';
import { AchievementSystem } from './AchievementSystem.js';
import { AchievementToast } from '../ui/AchievementToast.js';
import { AchievementModal } from '../ui/AchievementModal.js';
import { LeaderboardModal } from '../ui/LeaderboardModal.js';
import { apiService } from './ApiService.js';

/**
 * 游戏主控制器
 * 负责初始化和协调各个子系统
 */
export class Game {
    constructor(container, options = {}) {
        this.container = typeof container === 'string'
            ? document.getElementById(container)
            : container;

        if (!this.container) {
            throw new Error('Game container not found');
        }

        // 合并配置
        this.config = mergeConfig(Config, options);

        // 事件总线
        this.events = globalEventBus;

        // 子系统引用
        this.app = null;
        this.puzzleBoard = null;
        this.imageLoader = null;
        this.uiManager = null;
        this.timer = null;
        this.scoreBoard = null;
        this.dragController = null;

        // 游戏状态
        this.state = {
            isPlaying: false,
            isPaused: false,
            isComplete: false,
            currentImage: null
        };

        // 初始化Promise，供外部等待
        this.ready = this.init();

        // 暴露到全局，便于调试
        window.__PUZZLE_GAME__ = this;
    }

    /**
     * 初始化游戏
     */
    async init() {
        // 创建PIXI应用
        this.app = new PIXI.Application();

        await this.app.init({
            width: this.container.clientWidth,
            height: this.container.clientHeight,
            backgroundAlpha: this.config.renderer.backgroundAlpha,
            antialias: this.config.renderer.antialias,
            resolution: window.devicePixelRatio || 1, // Ensure high-DPI support
            autoDensity: true,
            preference: 'webgl',
            // powerPreference: 'high-performance',
            resizeTo: this.container
        });

        this.container.appendChild(this.app.canvas);

        // 初始化子系统
        this.initSubsystems();

        // 绑定事件
        this.bindEvents();

        // 处理窗口大小变化
        window.addEventListener('resize', () => this.onResize());

        console.log('Jigsaw Puzzle Game initialized');
    }

    /**
     * 初始化子系统
     */
    initSubsystems() {
        // 图片加载器
        this.imageLoader = new ImageLoader();

        // 计时器
        this.timer = new Timer(this.config.timer);

        // 计分板
        this.scoreBoard = new ScoreBoard(this.config.score);

        // UI管理器
        this.uiManager = new UIManager(this.app, this.config);

        // 拼图板
        this.puzzleBoard = new PuzzleBoard(this.app, this.config);

        // 拖拽控制器
        this.dragController = new DragController(this.app, this.puzzleBoard, this.config);

        // 成就系统
        this.achievementSystem = new AchievementSystem();
        this.achievementToast = new AchievementToast();
        this.achievementModal = new AchievementModal(this.achievementSystem);
        this.leaderboardModal = new LeaderboardModal();
    }

    /**
     * 绑定事件监听
     */
    bindEvents() {
        // 拼图完成事件
        this.events.on(GameEvents.PIECE_SNAP, (piece) => {
            this.scoreBoard.addSnapBonus();
            this.checkCompletion();
        });

        // 旋转事件
        this.events.on(GameEvents.PIECE_ROTATE, () => {
            this.scoreBoard.applyRotationPenalty();
        });

        // 游戏完成事件
        this.events.on(GameEvents.GAME_COMPLETE, () => {
            this.onGameComplete();
        });

        // 配置变更事件
        this.events.on(GameEvents.CONFIG_CHANGED, (newConfig) => {
            this.config = mergeConfig(this.config, newConfig);
        });
    }

    /**
     * 加载图片并开始游戏
     * @param {string|File} source 图片URL或文件对象
     */
    async loadImage(source) {
        try {
            const texture = await this.imageLoader.load(source);
            this.state.currentImage = texture;
            this.events.emit(GameEvents.IMAGE_LOADED, texture);
            return texture;
        } catch (error) {
            this.events.emit(GameEvents.IMAGE_ERROR, error);
            throw error;
        }
    }

    /**
     * 开始新游戏
     * @param {Object} options 游戏选项
     */
    async startGame(options = {}) {
        const gameConfig = mergeConfig(this.config.puzzle, options);

        // 重置状态
        this.state.isPlaying = true;
        this.state.isPaused = false;
        this.state.isComplete = false;

        // 重置计时器和分数
        this.timer.reset();
        this.scoreBoard.reset();

        // 生成拼图
        await this.puzzleBoard.generate(gameConfig, this.state.currentImage);

        // 确保初始位置正确 (有时PIXI宽高在init后才会更新)
        this.puzzleBoard.onResize();

        // 绑定拖拽事件
        this.dragController.enable();
        this.dragController.bindAllPieces();

        // 散落拼图块
        this.puzzleBoard.scatter();

        // 开始计时
        this.timer.start();

        this.events.emit(GameEvents.GAME_START);
        
        // 初始化排行榜小部件
        const urlParams = new URLSearchParams(window.location.search);
        const puzzleId = urlParams.get('id');
        if (puzzleId) {
            this.initLeaderboardWidget(puzzleId);
        }
    }

    /**
     * 初始化底部排行榜小部件
     */
    async initLeaderboardWidget(puzzleId) {
        const widget = document.getElementById('mini-leaderboard');
        const listContainer = document.getElementById('mini-lb-list');
        const moreBtn = document.getElementById('btn-show-more-lb');
        const likeBtn = document.getElementById('btn-like-puzzle');
        const likeCount = document.getElementById('like-count');

        if (!widget || !listContainer) return;

        // 显示组件
        widget.style.display = 'flex';
        listContainer.innerHTML = '<div class="mini-lb-loading">Loading...</div>';

        // 绑定更多按钮
        if (moreBtn) {
            moreBtn.onclick = () => {
                this.leaderboardModal.show(puzzleId);
            };
        }

        // 绑定点赞按钮
        if (likeBtn) {
            likeBtn.onclick = () => this.handleLikeClick(puzzleId);
        }

        // 1. 获取点赞数据
        try {
            const likeData = await apiService.getPuzzleLikes(puzzleId);
            if (likeCount) likeCount.textContent = likeData.total;
            if (likeBtn) {
                likeBtn.classList.toggle('liked', likeData.userLiked);
            }
        } catch (e) {
            console.error('Failed to load likes:', e);
        }

        // 2. 获取排行榜数据
        try {
            const data = await apiService.getLeaderboard(puzzleId);
            
            // 取前3名
            const top3 = data.slice(0, 3);

            if (top3.length === 0) {
                listContainer.innerHTML = '<div class="mini-lb-loading">No scores yet</div>';
                return;
            }

            // 渲染列表
            listContainer.innerHTML = top3.map((item, index) => {
                const rank = index + 1;
                let rankClass = '';
                if (rank === 1) rankClass = 'rank-1';
                if (rank === 2) rankClass = 'rank-2';
                if (rank === 3) rankClass = 'rank-3';

                const avatar = item.avatar_url || `https://ui-avatars.com/api/?name=${item.username}&background=random`;
                const timeStr = this.formatTime(item.time_ms);

                return `
                    <div class="mini-lb-item" title="${item.username} - ${timeStr}">
                        <div class="mini-lb-rank ${rankClass}">${rank}</div>
                        <img src="${avatar}" class="mini-lb-avatar" alt="${item.username}" referrerpolicy="no-referrer">
                        <span class="mini-lb-name">${item.username}</span>
                        <span class="mini-lb-time">${timeStr}</span>
                    </div>
                `;
            }).join('');

        } catch (error) {
            console.error('Failed to load mini leaderboard:', error);
            listContainer.innerHTML = '<div class="mini-lb-loading">Error</div>';
        }
    }

    async handleLikeClick(puzzleId) {
        if (!apiService.isLoggedIn()) {
            return; // ApiService already shows toast
        }

        const likeBtn = document.getElementById('btn-like-puzzle');
        const likeCount = document.getElementById('like-count');
        if (!likeBtn) return;

        // Optimistic UI update
        const currentlyLiked = likeBtn.classList.contains('liked');
        const currentTotal = parseInt(likeCount.textContent || '0');
        
        likeBtn.classList.toggle('liked', !currentlyLiked);
        likeCount.textContent = currentlyLiked ? currentTotal - 1 : currentTotal + 1;

        try {
            const result = await apiService.toggleLike(puzzleId);
            if (!result) {
                // Revert on failure
                likeBtn.classList.toggle('liked', currentlyLiked);
                likeCount.textContent = currentTotal;
                return;
            }
            // Update with real count just in case
            const updatedData = await apiService.getPuzzleLikes(puzzleId);
            likeCount.textContent = updatedData.total;
            likeBtn.classList.toggle('liked', updatedData.userLiked);
        } catch (e) {
            // Revert on error
            likeBtn.classList.toggle('liked', currentlyLiked);
            likeCount.textContent = currentTotal;
        }
    }

    formatTime(ms) {
        const totalSeconds = Math.floor(ms / 1000); 
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }

    /**
     * 暂停游戏
     */
    pause() {
        if (!this.state.isPlaying || this.state.isPaused) return;

        this.state.isPaused = true;
        this.timer.pause();
        this.dragController.disable();

        this.events.emit(GameEvents.GAME_PAUSE);
    }

    /**
     * 恢复游戏
     */
    resume() {
        if (!this.state.isPaused) return;

        this.state.isPaused = false;
        this.timer.resume();
        this.dragController.enable();

        this.events.emit(GameEvents.GAME_RESUME);
    }

    /**
     * 重置游戏
     */
    reset() {
        this.state.isPlaying = false;
        this.state.isPaused = false;
        this.state.isComplete = false;

        this.timer.reset();
        this.scoreBoard.reset();
        this.puzzleBoard.clear();

        this.events.emit(GameEvents.GAME_RESET);
    }

    /**
     * 检查拼图是否完成
     */
    checkCompletion() {
        if (this.state.isComplete) return; // 防止重复触发

        if (this.puzzleBoard.isComplete()) {
            this.state.isComplete = true;
            this.state.isPlaying = false;
            this.dragController.disable(); // 禁用拖拽，防止完成后再操作
            this.events.emit(GameEvents.GAME_COMPLETE);
        }
    }

    /**
     * 游戏完成处理
     */
    async onGameComplete() {
        this.timer.stop();
        const timeMs = this.timer.getElapsedMs(); // 获取毫秒

        // 计算时间奖励 (API可能只需要秒，但这里我们主要关注排行榜提交)
        const timeBonus = this.scoreBoard.calculateTimeBonus(timeMs / 1000);

        console.log('Puzzle Complete!', {
            time: this.timer.getFormattedTime(),
            score: this.scoreBoard.getScore(),
            timeBonus
        });

        // 尝试提交成绩并刷新排行榜
        const user = apiService.getCurrentUser();
        const urlParams = new URLSearchParams(window.location.search);
        const puzzleId = urlParams.get('id');

        // 检查是否是自定义图片 (自定义图片不提交排行榜)
        const isCustom = urlParams.get('custom') === 'true';

        if (user && puzzleId && !isCustom) {
            try {
                await apiService.submitScore(puzzleId, timeMs);
                console.log('Score submitted successfully:', timeMs);
                // 刷新排行榜
                this.initLeaderboardWidget(puzzleId);
            } catch (error) {
                console.error('Failed to submit score:', error);
            }
        }
    }

    /**
     * 将拼图排列成网格
     */
    arrangeGrid() {
        this.puzzleBoard.arrangeGrid();
    }

    /**
     * 散落拼图
     */
    scatter() {
        this.puzzleBoard.scatter();
        this.events.emit(GameEvents.PUZZLE_SCATTERED);
    }

    /**
     * 处理窗口大小变化
     */
    onResize() {
        if (this.puzzleBoard) {
            this.puzzleBoard.onResize();
        }
    }

    /**
     * 更新配置
     * @param {Object} newConfig 新配置
     */
    updateConfig(newConfig) {
        this.config = mergeConfig(this.config, newConfig);
        this.events.emit(GameEvents.CONFIG_CHANGED, this.config);
    }

    /**
     * 获取当前游戏状态
     * @returns {Object} 游戏状态
     */
    getState() {
        return {
            ...this.state,
            time: this.timer.getElapsed(),
            score: this.scoreBoard.getScore(),
            stats: this.scoreBoard.getStats(),
            progress: this.puzzleBoard ? this.puzzleBoard.getProgress() : 0
        };
    }

    /**
     * 销毁游戏实例
     */
    destroy() {
        this.events.clear();
        this.timer.stop();

        if (this.app) {
            this.app.destroy(true, { children: true, texture: true });
        }

        window.removeEventListener('resize', this.onResize);
    }
}

export default Game;
